import { formatPaymentMethod, formatMoney as money } from './money'

/**
 * The sentences a billing account's statement says, wherever it is read — the
 * email and the pay page set them differently and word them once, here. A
 * member who opens the link from the email reads the same words on the page.
 */

type Cadence = 'monthly' | 'annual'

const per = (cadence: Cadence) => (cadence === 'monthly' ? 'monthly' : 'yearly')
const unit = (cadence: Cadence, n: number) =>
  `${cadence === 'monthly' ? 'month' : 'year'}${n === 1 ? '' : 's'}`

const WORDS = [
  'no',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
]
/** A small count spelled, a larger one in figures — the way a letter reads. */
const count = (n: number) => WORDS[n] ?? String(n)

/** A figure in a statement's column: the currency sign is the heading's. */
export const statementFigure = (cents: number) => money(cents).replace('$', '')

/**
 * The fee a payer covered on a payment, said beside it. It is part of what the
 * card was charged and not of what the payment settled, so it is a note on the
 * line rather than a figure in the column.
 */
export const statementCoveredFee = (cents: number) =>
  `Card fee covered, ${money(cents)}`

/** A signed effect on what is owed: + raises it, − lowers it. */
export function statementSigned(cents: number): string {
  const body = statementFigure(Math.abs(cents))
  if (cents > 0) return `+${body}`
  if (cents < 0) return `−${body}`
  return body
}

/**
 * A running balance: a position rather than a movement, so it carries no sign;
 * money on account is named `CR`, the way `formatBalance` writes it on screen.
 */
export function statementRunning(cents: number): string {
  return cents < 0 ? `${statementFigure(-cents)} CR` : statementFigure(cents)
}

export const STATEMENT_STARTING_BALANCE = 'Starting balance'
export const STATEMENT_ACTIVITY = 'Activity'
export const STATEMENT_COLUMNS = ['Date', 'Item', 'Amount', 'Balance'] as const
export const STATEMENT_NEW_CHARGES = 'New charges'
export const STATEMENT_VOIDED = 'Voided'
export const STATEMENT_PAYMENTS = 'Payments'
export const STATEMENT_GIFTS = 'Gifts'
export const STATEMENT_REFUNDS = 'Refunds'
export const STATEMENT_CREDITS = 'Credits'
export const STATEMENT_MOVED = 'Moved between accounts'
export const STATEMENT_CORRECTION = 'Correction'
export const STATEMENT_BALANCE_DUE = 'Balance due'
export const STATEMENT_STILL_OPEN = 'Still open from before'
/** The ways to pay that skip the pay page — Zelle, a check. */
export const STATEMENT_PAY_BY_HAND = 'Other ways to pay'
export const STATEMENT_CORRECTION_LABEL = 'Correction to earlier entries'
/** What an overpayment left as a gift, named for whoever it was given to:
 *  "the shul" on a shul's statement, the org's own name elsewhere
 *  (`giftToFor`). */
export function statementGiftLabel(giftTo: string): string {
  return `Kept as a gift to ${giftTo}`
}
export const STATEMENT_CREDIT_LABEL = 'Credit'
export const STATEMENT_ADJUSTMENT_LABEL = 'Adjustment'
/** Money a won card dispute put back: on the line that says so, and on the
 *  credit it left on the account. */
export const STATEMENT_DISPUTE_WON_NOTE = 'Returned after a dispute was won'
export const STATEMENT_GIFT_NOTE = 'Not toward your balance'

/** A gift, named by what it was for — or just "Gift" where it was for
 *  nothing in particular, rather than "Gift: Gift". */
export function statementGivenLabel(label: string): string {
  return label === 'Gift' ? 'Gift' : `Gift: ${label}`
}

/** A payment's method and reference, named as an act in the office ledger. */
export function statementPaymentLineLabel(label: string): string {
  return `Payment — ${label}`
}

/**
 * Which column of the office's ledger a statement line's figure sits in, and
 * its size there: Pledged for what is owed or taken back off it, Paid for money
 * in or what came back off it. One answer, read by the cells and by the sort,
 * so a line cannot be drawn in one column and ordered as if it were in neither.
 */
export function ledgerColumns(line: {
  kind: string
  amountCents: number
  givenCents: number | null
}): { pledgedCents: number | null; paidCents: number | null } {
  switch (line.kind) {
    case 'charge':
    case 'void':
    case 'correction':
    case 'moved_charge':
      return { pledgedCents: line.amountCents, paidCents: null }
    case 'payment':
    case 'moved_payment':
    case 'credit':
    case 'refund':
    case 'gift':
    case 'guest':
      return { pledgedCents: null, paidCents: -line.amountCents }
    case 'given':
      return { pledgedCents: null, paidCents: line.givenCents ?? 0 }
    default:
      return { pledgedCents: null, paidCents: null }
  }
}

export function statementVoidLabel(label: string): string {
  return `Voided: ${label}`
}

/** What was still unpaid on a charge, let go when its arrangement closed. */
export function statementClearedLabel(label: string): string {
  return `Cleared: ${label}`
}

/** A charge's figure changed after it was raised: the line is the change. */
export function statementCorrectedLabel(label: string): string {
  return `Corrected: ${label}`
}

/** A charge or a payment that left this account for another, or for a
 *  pledge paid apart from it. */
export function statementMovedOffLabel(label: string): string {
  return `Moved off your account: ${label}`
}

/** A charge or a payment that came onto this account from another, or from
 *  a pledge paid apart from it. */
export function statementMovedOntoLabel(label: string): string {
  return `Moved onto your account: ${label}`
}

/** Money already on the account, moved onto a pledge outside it. */
export function statementMovedToLabel(target: string): string {
  return `Moved to ${target}`
}

/** Money that had gone to a pledge or a gift, back on the account. */
export function statementReturnedFromLabel(target: string): string {
  return `Returned to your account from ${target}`
}

/**
 * A payment's money moved between charges on the account: one is settled by
 * it and another owes it again, so the balance does not move and the line
 * carries no figure of its own.
 */
export function statementReappliedLabel(
  paidWith: string,
  cents: number,
  from: string[],
  to: string[],
): string {
  return `${paidWith}: ${money(cents)} moved from ${listed(from)} to ${listed(to)}`
}

/** Credit on the account, named as a thing money moved to or from. */
export const STATEMENT_CREDIT_TARGET = 'credit on your account'

/** "A", "A and B", "A, B and C". */
function listed(names: string[]): string {
  if (names.length < 2) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * A line's figure in a statement's Amount column: a gift's own amount, since
 * it never moves the balance; nothing for money moved between charges, which
 * moves nothing; otherwise the signed effect on what is owed.
 */
export function statementLineAmount(line: {
  kind: string
  amountCents: number
  givenCents: number | null
}): string {
  if (line.kind === 'given') return statementFigure(line.givenCents ?? 0)
  if (line.kind === 'reapplied') return ''
  return statementSigned(line.amountCents)
}

/** What a refund took back out of a pledge or a gift rather than the
 *  account. */
export function statementRefundedFromLabel(target: string): string {
  return `Refunded out of ${target}`
}

/** A gift a payment left, named as the thing money moves to or from. */
export function statementGiftTarget(giftTo: string): string {
  return `the gift to ${giftTo}`
}

/**
 * A pledge outside the running account, named as the thing money moved to or
 * from: a guest's by name, a pledge paid separately or another billing
 * account's by its own label, and one recorded with no billing account as
 * such.
 */
export function statementPledgeTarget(p: {
  track: 'guest' | 'separate' | 'account' | 'unplaced'
  label: string
  guestName: string | null
}): string {
  if (p.track === 'guest') {
    return p.guestName ? `${p.guestName}’s pledge` : 'a guest’s pledge'
  }
  if (p.track === 'unplaced') return 'a pledge not on your account'
  return p.label
}

export function statementPaymentLabel(
  method: string,
  reference: string | null,
): string {
  return `${formatPaymentMethod(method)}${reference ? ` ${reference}` : ''}`
}

export function statementRefundLabel(
  method: string,
  reference: string | null,
): string {
  return `Refund: ${statementPaymentLabel(method, reference)}`
}

export function statementCreditLabel(
  sourceType: 'comp' | 'adjustment',
): string {
  return sourceType === 'comp'
    ? STATEMENT_CREDIT_LABEL
    : STATEMENT_ADJUSTMENT_LABEL
}

/** Who pledged a charge, where that is somebody other than the head. */
export function pledgedByNote(pledgedBy: string | null): string | null {
  return pledgedBy ? `Pledged by ${pledgedBy}` : null
}

/** The closing total's label: Balance due, or the funded/settled wording. */
export function statementClosingLabel(netOwedCents: number): string {
  if (netOwedCents > 0) return STATEMENT_BALANCE_DUE
  return statementBalance(netOwedCents).lead
}

/** The window's own closing figure, on a window that ended before today. */
export const STATEMENT_PERIOD_END = 'Balance at the end of this period'

/**
 * The totals over a statement's ledger, in the order every surface sets them:
 * the starting balance, each kind of movement, and what it comes to. A kind
 * with no line in the window is left out rather than shown as zero; new
 * charges and payments always show, since a reader looks for both.
 *
 * A window that ended before today closes on its own balance, and today's
 * follows it as the figure that counts — a past period that ended settled
 * must not read as nothing owed when something is.
 */
export function statementTotals(s: {
  openingCents: number
  /** The balance at the end of the window — what the lines add up to. */
  closingCents: number
  /** What is owed today. */
  netOwedCents: number
  /** The window ended before today, so its close is not today's balance. */
  periodEnded: boolean
  lines: { givenCents: number | null }[]
  totals: {
    chargesCents: number
    voidedCents: number | null
    paymentsCents: number
    refundsCents: number | null
    creditsCents: number | null
    movedCents: number | null
    correctionCents: number | null
  }
}): { label: string; amount: string; closing?: true }[] {
  const t = s.totals
  const giftsCents = s.lines.reduce(
    (sum, line) => sum + (line.givenCents ?? 0),
    0,
  )
  const hasGifts = s.lines.some((line) => line.givenCents !== null)
  const movements: [string, number | null][] = [
    [STATEMENT_NEW_CHARGES, t.chargesCents],
    [STATEMENT_VOIDED, t.voidedCents],
    [STATEMENT_PAYMENTS, t.paymentsCents],
    [STATEMENT_GIFTS, hasGifts ? giftsCents : null],
    [STATEMENT_REFUNDS, t.refundsCents],
    [STATEMENT_CREDITS, t.creditsCents],
    [STATEMENT_MOVED, t.movedCents],
    [STATEMENT_CORRECTION, t.correctionCents],
  ]
  return [
    {
      label: STATEMENT_STARTING_BALANCE,
      amount: statementRunning(s.openingCents),
    },
    ...movements.flatMap(([label, cents]) =>
      cents === null
        ? []
        : [
            {
              label,
              amount:
                label === STATEMENT_GIFTS
                  ? statementFigure(cents)
                  : statementSigned(cents),
            },
          ],
    ),
    ...(!s.periodEnded
      ? []
      : [
          {
            label: STATEMENT_PERIOD_END,
            amount: statementRunning(s.closingCents),
          },
        ]),
    {
      label: s.periodEnded
        ? `${statementClosingLabel(s.netOwedCents)} today`
        : statementClosingLabel(s.netOwedCents),
      amount: statementBalance(s.netOwedCents).figure,
      closing: true as const,
    },
  ]
}

/** First day to last day of a prepaid stretch, in the date style a statement
 *  line already uses. Same-year ends omit the year; a stretch that crosses
 *  years names both. */
export function statementStretch(firstIso: string, lastIso: string): string {
  const yearOf = (iso: string) => iso.slice(0, 4)
  const day = (iso: string, year: string) =>
    statementDay(`${iso}T12:00:00.000Z`, 'UTC', year)
  const y1 = yearOf(firstIso)
  const y2 = yearOf(lastIso)
  if (y1 === y2) return `${day(firstIso, y1)} – ${day(lastIso, y1)}`
  return `${day(firstIso, '')} – ${day(lastIso, '')}`
}

/**
 * A period charge's item line. A count of 1 is the item's name, as it is now;
 * a count above 1 names the stretch the one charge covers.
 */
export function statementPeriodChargeLabel(
  label: string,
  periodCount: number,
  firstIso: string | null,
  lastIso: string | null,
): string {
  if (!firstIso || !lastIso) return label
  if (periodCount === 1) return label
  if (periodCount < 1 && firstIso === lastIso) return label
  return `${label} · ${statementStretch(firstIso, lastIso)}`
}

/** A line's date — "12 Mar" — with the year only when it is not the
 *  statement's own. */
export function statementDay(
  iso: string,
  timeZone: string,
  statementYear: string,
): string {
  const d = new Date(iso)
  const part = (o: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString('en-US', { timeZone, ...o })
  // Day before month, in American month names: "12 Sep", never "12 Sept".
  const dm = `${part({ day: 'numeric' })} ${part({ month: 'short' })}`
  const y = part({ year: 'numeric' })
  return y === statementYear ? dm : `${dm} ${y}`
}

/** The signed balance, with a funded account stated as an ordinary state. */
export function statementBalance(netOwedCents: number): {
  lead: string
  figure: string
} {
  if (netOwedCents === 0) {
    return { lead: 'Your account is settled', figure: money(0) }
  }
  return netOwedCents > 0
    ? { lead: 'You owe', figure: money(netOwedCents) }
    : { lead: 'You have on account', figure: money(-netOwedCents) }
}

/** "2 months not covered", "1 year not covered". */
export const notCovered = (periods: number, cadence: Cadence) =>
  `${periods} ${unit(cadence, periods)} not covered`

/** A standing arrangement as a rate and its span — never a bare figure. */
export function standingTerms(a: {
  rateCents: number
  cadence: Cadence
  endsOn: string | null
  paidThrough: string | null
}): string {
  const rate = `${money(a.rateCents)} ${per(a.cadence)}`
  const span = a.endsOn
    ? `until ${new Date(`${a.endsOn}T12:00:00Z`).toLocaleDateString('en-US', {
        timeZone: 'UTC',
        month: 'short',
        year: 'numeric',
      })}`
    : 'ongoing'
  const coverage = a.paidThrough
    ? ` · Membership paid through ${new Date(
        `${a.paidThrough}T12:00:00Z`,
      ).toLocaleDateString('en-US', {
        timeZone: 'UTC',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })}`
    : ''
  return `${rate}, ${span}${coverage}`
}

/**
 * A debt paid down at a rate, as its two true facts: what is left, and whether
 * it is behind.
 */
export function planTerms(p: {
  rateCents: number
  cadence: Cadence
  totalCents: number | null
  owedCents: number
  periodsBehind: number
}): string {
  const parts = [
    p.totalCents === null
      ? `${money(p.rateCents)} ${per(p.cadence)} toward what you owe`
      : `${money(p.rateCents)} ${per(p.cadence)} toward ${money(p.totalCents)}`,
  ]
  if (p.totalCents !== null) parts.push(`${money(p.owedCents)} to go`)
  if (p.periodsBehind > 0) {
    parts.push(
      `${count(p.periodsBehind)} ${unit(p.cadence, p.periodsBehind)} behind`,
    )
  }
  return parts.join(' · ')
}

/** How long credit on hand lasts at the rate the account has been charged. */
export function runwaySentence(months: number): string {
  if (months < 1) {
    return 'At this rate your account will not cover another month.'
  }
  return `At this rate your account covers about ${count(months)} more ${unit('monthly', months)}.`
}

/** A payment's part that went to a pledge not on this account: a guest's by
 *  name, or one recorded with no billing account. */
export function towardPledge(
  forGuest: boolean,
  guestName: string | null,
): string {
  if (!forGuest) return 'Toward a pledge not on your account'
  return guestName ? `Toward ${guestName}’s pledge` : 'Toward a guest’s pledge'
}

/**
 * Where a pledge paid separately stands. Money received toward it in this
 * statement is acknowledged against the pledge, since it did not move the
 * balance above; a plan on it says its rate and whether it is behind.
 */
export function separatePledgeTerms(p: {
  amountCents: number
  paidCents: number
  owedCents: number
  paidInWindowCents: number
  plan: { rateCents: number; cadence: Cadence; periodsBehind: number } | null
}): string {
  if (p.plan) {
    const terms = planTerms({
      ...p.plan,
      totalCents: p.amountCents,
      owedCents: p.owedCents,
    })
    return p.paidInWindowCents > 0
      ? `${money(p.paidInWindowCents)} received · ${terms}`
      : terms
  }
  const lead =
    p.paidInWindowCents > 0
      ? `${money(p.paidInWindowCents)} of your ${money(p.amountCents)} pledge`
      : `${money(p.paidCents)} paid of ${money(p.amountCents)}`
  return `${lead} · ${money(p.owedCents)} to go`
}

/** A pledge paid separately is settled only by money given for it. */
export const SEPARATE_NOTE =
  'Paid on their own, only by money given for them — never from your account. Not part of your balance above.'

/** What the expected frame says, naming whoever looks for the money. */
export function expectedNote(giftTo: string): string {
  return `This is what ${giftTo} looks for, and it is not part of the balance above. Nothing is owed, and any amount covers it.`
}

/** The pay link settles the billing account's own balance, so a guest's
 *  pledge is paid through the office. */
export const GUEST_NOTE =
  'Pledged by guests you brought. Not part of your balance above, and not asked of you. It is paid through the office, by your guest or by you.'
